import FormField from "@components/Form/FormFields/FormFields";
import formStyles from "@components/Form/FormFields/FormFields.module.scss";
import useOnClickOutside from "@utilities/hooks/useOnClickOutside";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import styles from "./InstitutionSection.module.scss";
import { InstitutionSectionProps } from "./interfaces";

const InstitutionSection = (props: InstitutionSectionProps) => {
  const {
    value,
    register,
    errors,
    name,
    validation,
    placeholder,
    options,
    setValue,
    clearErrors,
    watch,
    selectDescription,
    inputDescription,
    trigger,
  } = props;
  const [filteredOptions, setFilteredOptions] = useState(options);

  const [isFocused, setIsFocused] = useState(false);
  const [isShowOptions, setShowOptions] = useState(false);

  const notFoundItem = { id: "none", value: "None of these" };

  const getFilteredOptions = (inputValue) => {
    const result = options.filter((item) =>
      item?.value?.toLowerCase().includes(inputValue.toLowerCase())
    );
    if (result.findIndex((item) => item === notFoundItem) === -1) {
      result.push(notFoundItem);
    }

    setFilteredOptions(result);
  };

  const hasError = errors && errors[name];

  useEffect(() => {
    setIsFocused(!!value);
  }, [value]);

  const handleOptionSelect = (option) => {
    setValue(name, option.value);
    setValue(name + "FreeText", "");
    clearErrors(name);
    setIsFocused(!!value);
    setShowOptions(false);
  };

  const handleOnBlur = () => {
    setIsFocused(!!value);
    setShowOptions(false);
  };

  const handleOnFocus = () => {
    setIsFocused(true);
  };

  const handleInputOnChange = (e) => {
    setValue(name, e.target.value);
    getFilteredOptions(e.target.value);
    setShowOptions(true);
  };

  const fieldRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(fieldRef, () => handleOnBlur());

  const renderOptions = () => {
    return (
      <ul className={styles["countries-wrapper"]}>
        {filteredOptions?.map((option, index) => (
          <li
            key={`${option.id}${index}`}
            className={styles["countries-item"]}
            value={option.id}
            onClick={() => handleOptionSelect(option)}
          >
            {option.value}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      <p>{selectDescription}</p>
      <div
        className={clsx(
          "form",
          "form-row",
          styles["form-row"],
          formStyles["form-row"],
          "field-row",
          styles["country-section"],
          isFocused && "focused",
          isFocused && formStyles.focused,
          hasError && "error",
          hasError && formStyles.error
        )}
      >
        <div className={formStyles["form-label"]}>
          <div className={formStyles.label}>
            <label htmlFor={name}>{placeholder}</label>
          </div>
        </div>
        <div className={formStyles["form-field"]}>
          <div className={formStyles.field} ref={fieldRef}>
            <>
              <input
                id={name}
                name={name}
                className={clsx("smart-focus", styles["homecountry-box"])}
                type="text"
                value={value}
                autoComplete="off"
                onFocus={handleOnFocus}
                {...register(name, {
                  onChange: handleInputOnChange,
                  ...validation,
                })}
                aria-invalid={hasError}
                aria-describedby={hasError ? `${name}-message` : undefined}
              />
              {isShowOptions && options?.length > 0 && renderOptions()}
            </>
          </div>

          {hasError && (
            <div className="form-row error">
              <div className="form-field base6">
                <div
                  className={clsx(
                    formStyles["message-wrapper"],
                    "field",
                    "message-wrapper",
                    "inline-message",
                    "field_error_msg"
                  )}
                  id={`${name}-message`}
                >
                  <p className={clsx(formStyles.message, formStyles.active)}>
                    <span className="sr-only">Error: </span>
                    {errors[name]?.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {value === notFoundItem.value && (
        <div>
          <p>{inputDescription}</p>
          <FormField
            watch={watch}
            validation={{
              isRequired: true,
            }}
            type={"text"}
            register={register}
            trigger={trigger}
            key={`institution-free-text`}
            setSelectValue={setValue}
            placeholder={placeholder}
            name={name + "FreeText"}
            // @ts-expect-error type mistmatch
            errors={errors}
          />
        </div>
      )}
    </div>
  );
};

export default InstitutionSection;
